import React, { useEffect, useRef, useState } from "react";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { formatDateTime } from "@/lib/dateTime";
import { Loader2, ImagePlus } from "lucide-react";

type Margins = { top: number; bottom: number; left: number; right: number };
type Letterhead = {
  active: null | { id: string; version: number; created_on: string; width_px: number; height_px: number; dpi: number; has_later: boolean; first_margins: Margins; later_margins: Margins };
  on_quotes: boolean;
  on_proposals: boolean;
  on_agreements: boolean;
  defaults: { first: Margins; later: Margins };
  history: { id: string; version: number; created_on: string }[];
};

const SIDES: { key: keyof Margins; label: string }[] = [
  { key: "top", label: "Top" },
  { key: "bottom", label: "Bottom" },
  { key: "left", label: "Left" },
  { key: "right", label: "Right" },
];

/** A4 page with the artwork behind and the area text can use shaded on top */
function Page({ src, m, label }: { src: string; m: Margins; label: string }) {
  return (
    <figure className="min-w-0 flex-1 space-y-1">
      <div className="relative w-full overflow-hidden rounded-md border border-border bg-white" style={{ aspectRatio: "210 / 297" }}>
        <img src={src} alt={label} className="absolute inset-0 h-full w-full object-fill" />
        <div
          className="absolute rounded-[2px] border border-success/70 bg-success/10"
          style={{ top: `${(m.top / 297) * 100}%`, bottom: `${(m.bottom / 297) * 100}%`, left: `${(m.left / 210) * 100}%`, right: `${(m.right / 210) * 100}%` }}
        />
      </div>
      <figcaption className="text-center text-[11.5px] text-muted-foreground">{label}</figcaption>
    </figure>
  );
}

const fromInputs = (v: Record<keyof Margins, string>): Margins => ({ top: Number(v.top), bottom: Number(v.bottom), left: Number(v.left), right: Number(v.right) });
const toInputs = (m: Margins) => ({ top: String(m.top), bottom: String(m.bottom), left: String(m.left), right: String(m.right) });

function MarginInputs({ title, value, onChange }: { title: string; value: Record<keyof Margins, string>; onChange: (v: Record<keyof Margins, string>) => void }) {
  return (
    <div className="space-y-1.5">
      <p className="text-[12.5px] font-medium">{title}</p>
      <div className="grid grid-cols-4 gap-2">
        {SIDES.map((s) => (
          <label key={s.key} className="space-y-1 text-[11.5px] text-muted-foreground">
            {s.label}
            <Input type="number" min={5} max={130} step={1} value={value[s.key]} onChange={(e) => onChange({ ...value, [s.key]: e.target.value })} className="h-8 px-2 text-[13px]" />
          </label>
        ))}
      </div>
    </div>
  );
}

/** Settings → Company: the artwork quotes, proposals and agreements are printed on, and where text may go on it */
export function LetterheadCard() {
  const { success, error } = useToast();
  const [data, setData] = useState<Letterhead | null>(null);
  const [busy, setBusy] = useState(false);
  const [rev, setRev] = useState(0);
  const [first, setFirst] = useState<File | null>(null);
  const [later, setLater] = useState<File | null>(null);
  const [m1, setM1] = useState<Record<keyof Margins, string>>(toInputs({ top: 50, bottom: 52, left: 18, right: 18 }));
  const [m2, setM2] = useState<Record<keyof Margins, string>>(toInputs({ top: 33, bottom: 24, left: 18, right: 18 }));
  const firstRef = useRef<HTMLInputElement>(null);
  const laterRef = useRef<HTMLInputElement>(null);

  const apply = (d: Letterhead) => {
    setData(d);
    setM1(toInputs(d.active?.first_margins ?? d.defaults.first));
    setM2(toInputs(d.active?.later_margins ?? d.defaults.later));
    setRev((r) => r + 1);
  };

  useEffect(() => {
    api.get("/api/Company/Letterhead").then((r) => apply(unwrapObject<Letterhead>(r))).catch((e: any) => error("Could not load the letterhead", e.message || ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A just-picked file is previewed from the browser before it is uploaded
  const [blob1, setBlob1] = useState<string | null>(null);
  const [blob2, setBlob2] = useState<string | null>(null);
  useEffect(() => {
    if (!first) return setBlob1(null);
    const u = URL.createObjectURL(first);
    setBlob1(u);
    return () => URL.revokeObjectURL(u);
  }, [first]);
  useEffect(() => {
    if (!later) return setBlob2(null);
    const u = URL.createObjectURL(later);
    setBlob2(u);
    return () => URL.revokeObjectURL(u);
  }, [later]);

  const run = async (fn: () => Promise<void>, fail: string) => {
    setBusy(true);
    try {
      await fn();
    } catch (e: any) {
      error(fail, e.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const pick = (f: File | undefined, set: (f: File | null) => void) => {
    if (!f) return;
    if (!/^image\/(png|jpeg)$/.test(f.type)) return error("Invalid file", "Use an A4 portrait PNG or JPG");
    if (f.size > 10 * 1024 * 1024) return error("File too large", "The picture must be 10 MB or smaller");
    set(f);
  };

  const margins = () => ({ first_margins: fromInputs(m1), later_margins: fromInputs(m2) });

  const upload = () =>
    run(async () => {
      const fd = new FormData();
      if (first) fd.append("first", first);
      if (later) fd.append("later", later);
      fd.append("first_margins", JSON.stringify(margins().first_margins));
      fd.append("later_margins", JSON.stringify(margins().later_margins));
      const res = unwrapObject<Letterhead & { warnings?: string[] }>(await api.upload("/api/Company/Letterhead", fd));
      apply(res);
      setFirst(null);
      setLater(null);
      success("Letterhead saved", res.warnings?.[0] || "New quotes, proposals and agreements print on it");
    }, "Upload failed");

  const saveMargins = () =>
    run(async () => {
      apply(unwrapObject<Letterhead>(await api.patch("/api/Company/Letterhead/Margins", margins())));
      success("Margins saved", "Documents already sent keep the margins they were sent with");
    }, "Could not save the margins");

  const setUse = (patch: { on_quotes?: boolean; on_proposals?: boolean; on_agreements?: boolean }) =>
    run(async () => apply(unwrapObject<Letterhead>(await api.patch("/api/Company/Letterhead/Use", patch))), "Could not change that");

  const remove = () =>
    run(async () => {
      apply(unwrapObject<Letterhead>(await api.delete("/api/Company/Letterhead")));
      success("Letterhead switched off", "Documents print without it. Earlier versions are kept.");
    }, "Remove failed");

  const active = data?.active ?? null;
  const differs = (a: Margins, b: Margins) => SIDES.some((x) => a[x.key] !== b[x.key]);
  const marginsDirty = !!active && (differs(fromInputs(m1), active.first_margins) || differs(fromInputs(m2), active.later_margins));
  const marginsValid = [...Object.values(m1), ...Object.values(m2)].every((v) => v !== "" && Number.isFinite(Number(v)));
  const src1 = blob1 || (active ? `/api/Company/Letterhead/Image/first?v=${rev}` : null);
  const src2 = blob2 || (active?.has_later ? `/api/Company/Letterhead/Image/later?v=${rev}` : src1);

  return (
    <section className="space-y-3 rounded-[10px] border border-border bg-card px-4 py-3.5">
      <div>
        <h2 className="text-sm font-semibold text-foreground">Letterhead</h2>
        <p className="text-xs text-muted-foreground">The artwork behind every quote and proposal. The green area is where text may go.</p>
      </div>

      {!data ? (
        <div className="flex justify-center py-6"><Loader2 className="size-4 animate-spin text-muted-foreground" /></div>
      ) : (
        <>
          {src1 ? (
            <div className="flex gap-3">
              <Page src={src1} m={fromInputs(m1)} label="First page" />
              {src2 && <Page src={src2} m={fromInputs(m2)} label={first || later || active?.has_later ? "Following pages" : "Following pages (same picture)"} />}
            </div>
          ) : (
            <p className="rounded-md border border-dashed border-border px-3 py-6 text-center text-[12.5px] text-muted-foreground">No letterhead yet. Documents print with a plain header.</p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <input ref={firstRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => { pick(e.target.files?.[0], setFirst); e.target.value = ""; }} />
            <input ref={laterRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => { pick(e.target.files?.[0], setLater); e.target.value = ""; }} />
            <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => firstRef.current?.click()}>
              <ImagePlus className="size-4" />
              {first ? first.name : active ? "Replace first page" : "Upload first page"}
            </Button>
            <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => laterRef.current?.click()}>
              <ImagePlus className="size-4" />
              {later ? later.name : "Following pages picture"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">A4 portrait PNG or JPG, up to 10 MB. 2480 × 3508 px (300 DPI) is best; 1240 px wide is the least. Without a second picture, the first is used on every page.</p>

          <MarginInputs title="First page margins (mm)" value={m1} onChange={setM1} />
          <MarginInputs title="Following pages margins (mm)" value={m2} onChange={setM2} />

          <div className="flex flex-wrap items-center gap-2">
            {(first || later || !active) && (
              <Button type="button" size="sm" disabled={busy || !marginsValid || (!first && !active)} onClick={upload}>
                {busy ? <Loader2 className="size-4 animate-spin" /> : "Save letterhead"}
              </Button>
            )}
            {!first && !later && active && (
              <Button type="button" size="sm" disabled={busy || !marginsDirty || !marginsValid} onClick={saveMargins}>
                {busy ? <Loader2 className="size-4 animate-spin" /> : "Save margins"}
              </Button>
            )}
            {(first || later) && (
              <button type="button" className="text-xs font-medium text-muted-foreground hover:underline" onClick={() => { setFirst(null); setLater(null); }}>
                Cancel
              </button>
            )}
            {active && (
              <button type="button" disabled={busy} onClick={remove} className="ml-auto text-xs font-medium text-danger hover:underline">
                Switch off
              </button>
            )}
          </div>

          {active && (
            <div className="space-y-1.5 border-t border-border/60 pt-2.5">
              {[
                { label: "Print quotations on it", checked: data.on_quotes, patch: { on_quotes: !data.on_quotes } },
                { label: "Print proposals on it", checked: data.on_proposals, patch: { on_proposals: !data.on_proposals } },
                { label: "Print agreements on it", checked: data.on_agreements, patch: { on_agreements: !data.on_agreements } },
              ].map((s) => (
                <label key={s.label} className="flex items-center justify-between text-[13px]">
                  {s.label}
                  <Switch checked={s.checked} disabled={busy} onCheckedChange={() => setUse(s.patch)} aria-label={s.label} />
                </label>
              ))}
              <p className="text-xs text-muted-foreground">
                Version {active.version}, {formatDateTime(active.created_on, { day: "numeric", month: "short", year: "numeric" })}, about {active.dpi} DPI. A quote, proposal or agreement keeps the version it was sent on.
              </p>
            </div>
          )}
        </>
      )}
    </section>
  );
}
